import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import CreateStoreModal from './CreateStoreModal';
import DeleteStoreModal from './DeleteStoreModal';
import './Dashboard.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

// SVG Icons
const StoreIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9,22 9,12 15,12 15,22"/>
  </svg>
);

const TableIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M3 9h18M3 15h18M9 3v18"/>
  </svg>
);

const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
  </svg>
);

const MoreIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const OrdersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
    <polyline points="14,2 14,8 20,8"/>
    <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2"/>
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
    <polyline points="15,3 21,3 21,9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const PublishIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="17,1 21,5 17,9"/>
    <path d="M3 11V9a4 4 0 014-4h14"/>
    <polyline points="7,23 3,19 7,15"/>
    <path d="M21 13v2a4 4 0 01-4 4H3"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);

const ArchiveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="21,8 21,21 3,21 3,8"/>
    <rect x="1" y="3" width="22" height="5"/>
    <line x1="10" y1="12" x2="14" y2="12"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3,6 5,6 21,6"/>
    <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15,18 9,12 15,6"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9,18 15,12 9,6"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const FilterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
    <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
    <circle cx="4" cy="12" r="2"/><circle cx="12" cy="10" r="2"/><circle cx="20" cy="14" r="2"/>
  </svg>
);

const Dashboard = () => {
  const navigate = useNavigate();

  // Existing state
  const [teamStores, setTeamStores] = useState([]);
  const [archivedStores, setArchivedStores] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [invoicesGenerating, setInvoicesGenerating] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [completeModal, setCompleteModal] = useState({ show: false, type: null });
  const [stats, setStats] = useState({ totalStores: 0, activeStores: 0 });
  const [notification, setNotification] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteModalStore, setDeleteModalStore] = useState(null);

  // Run state - for filtering orders by store open/close cycles
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null); // null = current run, 'all' = all runs

  // New state for view mode, search, filter, sort, pagination
  const [viewMode, setViewMode] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCreator, setFilterCreator] = useState('all');
  const [creators, setCreators] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  // Mobile bottom sheet state
  const [bottomSheetStore, setBottomSheetStore] = useState(null);

  // Handle dropdown toggle with position calculation
  const handleDropdownToggle = (storeId, event) => {
    if (openDropdownId === storeId) {
      setOpenDropdownId(null);
      return;
    }

    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    // Position dropdown below and to the left of the button
    setDropdownPos({
      top: rect.bottom + 5,
      left: rect.right - 180, // 180px is the dropdown width
    });
    setOpenDropdownId(storeId);
  };

  // Fetch team stores and creators on mount
  useEffect(() => {
    fetchTeamStores();
    fetchCreators();
  }, []);

  // Refetch when creator filter changes
  useEffect(() => {
    fetchTeamStores();
  }, [filterCreator]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check both the trigger button and the portal-rendered dropdown menu
      if (openDropdownId &&
          !event.target.closest('.tsm-actions-trigger') &&
          !event.target.closest('.tsm-dropdown-portal')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // Lock body scroll when bottom sheet is open
  useEffect(() => {
    if (bottomSheetStore) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [bottomSheetStore]);

  // Close bottom sheet
  const closeBottomSheet = () => setBottomSheetStore(null);

  // Handle mobile card tap
  const handleMobileCardTap = (store) => {
    setBottomSheetStore(store);
  };

  // Get status counts for pills
  const getStatusCounts = () => {
    const all = teamStores.length;
    const active = teamStores.filter(s => s.status === 'active').length;
    const draft = teamStores.filter(s => s.status === 'draft').length;
    const closed = teamStores.filter(s => s.status === 'closed').length;
    return { all, active, draft, closed };
  };

  const fetchTeamStores = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCreator && filterCreator !== 'all') {
        params.append('created_by', filterCreator);
      }
      const url = `${API_BASE}/team-stores/admin${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTeamStores(data.stores || []);

        const stores = data.stores || [];
        const activeStores = stores.filter(s => s.status === 'active');
        setStats({
          totalStores: stores.length,
          activeStores: activeStores.length
        });
      }
    } catch (error) {
      console.error('Error fetching team stores:', error);
      showNotification('Error loading team stores', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreators = async () => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/creators`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCreators(data.creators || []);
      }
    } catch (error) {
      console.error('Error fetching creators:', error);
    }
  };

  const fetchOrders = async (storeSlug, runId = null) => {
    try {
      setOrdersLoading(true);
      const params = new URLSearchParams();
      if (runId !== null) {
        params.append('run_id', runId);
      }
      const url = `${API_BASE}/team-stores/${storeSlug}/orders${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
        setRuns(data.runs || []);
        // Set selected run to what the server returned (either current run or what was requested)
        setSelectedRunId(data.selectedRunId);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showNotification('Error loading orders', 'error');
    } finally {
      setOrdersLoading(false);
    }
  };

  const generateReport = async (storeSlug, runId = null) => {
    try {
      setReportGenerating(true);
      showNotification('Generating report...', 'info');

      const params = new URLSearchParams();
      if (runId !== null) {
        params.append('run_id', runId);
      }
      const url = `${API_BASE}/team-stores/${storeSlug}/report${params.toString() ? '?' + params.toString() : ''}`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        // Use the filename from Content-Disposition header if available
        const contentDisposition = response.headers.get('Content-Disposition');
        const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
        a.download = filenameMatch ? filenameMatch[1] : `${storeSlug}-fulfillment-report.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
        showNotification('Report generated successfully!', 'success');
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error generating report', 'error');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      showNotification('Error generating report', 'error');
    } finally {
      setReportGenerating(false);
    }
  };

  const printInvoices = async (storeSlug, runId = null) => {
    try {
      setInvoicesGenerating(true);
      showNotification('Generating invoices...', 'info');

      const params = new URLSearchParams();
      if (runId !== null) {
        params.append('run_id', runId);
      }
      const url = `${API_BASE}/team-stores/${storeSlug}/invoices${params.toString() ? '?' + params.toString() : ''}`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        // Use the filename from Content-Disposition header if available
        const contentDisposition = response.headers.get('Content-Disposition');
        const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
        a.download = filenameMatch ? filenameMatch[1] : `${storeSlug}-invoices.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
        showNotification('Invoices generated successfully!', 'success');
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error generating invoices', 'error');
      }
    } catch (error) {
      console.error('Error generating invoices:', error);
      showNotification('Error generating invoices', 'error');
    } finally {
      setInvoicesGenerating(false);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAllOrders = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.id));
    }
  };

  const handleMarkSelectedComplete = async () => {
    if (selectedOrders.length === 0) return;
    setCompleteModal({ show: true, type: 'selected' });
  };

  const handleMarkAllComplete = async () => {
    setCompleteModal({ show: true, type: 'all' });
  };

  const confirmMarkComplete = async () => {
    try {
      setMarkingComplete(true);
      const isAll = completeModal.type === 'all';

      const url = isAll
        ? `${API_BASE}/team-stores/${selectedStore.slug}/orders/complete-all`
        : `${API_BASE}/team-stores/${selectedStore.slug}/orders/complete`;

      const options = {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      };

      if (!isAll) {
        options.body = JSON.stringify({ orderIds: selectedOrders });
      }

      const response = await fetch(url, options);

      if (response.ok) {
        const data = await response.json();
        const successCount = data.results.success.length;
        const failedCount = data.results.failed.length;

        if (failedCount > 0) {
          showNotification(
            `${successCount} orders completed, ${failedCount} failed`,
            successCount > 0 ? 'warning' : 'error'
          );
        } else {
          showNotification(`${successCount} orders marked as completed!`, 'success');
        }

        setSelectedOrders([]);
        fetchOrders(selectedStore.slug);
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error completing orders', 'error');
      }
    } catch (error) {
      console.error('Error completing orders:', error);
      showNotification('Error completing orders', 'error');
    } finally {
      setMarkingComplete(false);
      setCompleteModal({ show: false, type: null });
    }
  };

  const handlePublishStore = async (storeId) => {
    setOpenDropdownId(null);
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/publish`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        showNotification('Store published successfully!', 'success');
        fetchTeamStores();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error publishing store', 'error');
      }
    } catch (error) {
      showNotification('Error publishing store', 'error');
    }
  };

  const handleCloseStore = async (storeId) => {
    setOpenDropdownId(null);
    if (!window.confirm('Are you sure you want to close this store?')) return;
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/close`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        showNotification('Store closed successfully!', 'success');
        fetchTeamStores();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error closing store', 'error');
      }
    } catch (error) {
      showNotification('Error closing store', 'error');
    }
  };

  const handleArchiveStore = async (storeId) => {
    setOpenDropdownId(null);
    if (!window.confirm('Are you sure you want to archive this store?')) return;
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        showNotification('Store archived successfully!', 'success');
        fetchTeamStores();
        if (showArchived) fetchArchivedStores();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error archiving store', 'error');
      }
    } catch (error) {
      showNotification('Error archiving store', 'error');
    }
  };

  const fetchArchivedStores = async () => {
    try {
      setArchivedLoading(true);
      const response = await fetch(`${API_BASE}/team-stores/admin/archived`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setArchivedStores(data.stores || []);
      }
    } catch (error) {
      console.error('Error fetching archived stores:', error);
      showNotification('Error loading archived stores', 'error');
    } finally {
      setArchivedLoading(false);
    }
  };

  const handleUnarchiveStore = async (storeId) => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/unarchive`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        showNotification('Store restored to draft!', 'success');
        fetchTeamStores();
        fetchArchivedStores();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error restoring store', 'error');
      }
    } catch (error) {
      showNotification('Error restoring store', 'error');
    }
  };

  const confirmDeleteStore = async () => {
    if (!deleteModalStore) return;
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${deleteModalStore.id}/permanent`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        showNotification('Store permanently deleted!', 'success');
        setDeleteModalStore(null);
        fetchTeamStores();
        fetchArchivedStores();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error deleting store', 'error');
        setDeleteModalStore(null);
      }
    } catch (error) {
      showNotification('Error deleting store', 'error');
      setDeleteModalStore(null);
    }
  };

  const handleToggleArchived = () => {
    if (!showArchived && archivedStores.length === 0) {
      fetchArchivedStores();
    }
    setShowArchived(!showArchived);
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleStoreSelect = (store) => {
    setOpenDropdownId(null);
    setSelectedStore(store);
    setSelectedOrders([]);
    fetchOrders(store.slug);
  };

  const handleBackToStores = () => {
    setSelectedStore(null);
    setOrders([]);
    setSelectedOrders([]);
    setRuns([]);
    setSelectedRunId(null);
  };

  const handleRunChange = (runId) => {
    setSelectedRunId(runId);
    setSelectedOrders([]);
    fetchOrders(selectedStore.slug, runId);
  };

  const formatRunLabel = (run) => {
    const openDate = new Date(run.openedAt).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
    const closeDate = run.closedAt
      ? new Date(run.closedAt).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric'
        })
      : 'Present';
    return `${openDate} – ${closeDate} (${run.orderCount} orders)`;
  };

  const handleEditStore = (storeId) => {
    setOpenDropdownId(null);
    navigate(`/team-store-management/edit/${storeId}`);
  };

  const handleCreateSuccess = (newStore) => {
    setIsCreateModalOpen(false);
    showNotification('Store created successfully!', 'success');
    fetchTeamStores();
    navigate(`/team-store-management/edit/${newStore.id}`);
  };

  const handleCopyStoreUrl = async (slug) => {
    const url = `https://fitness1stsports.com/?team_store=${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      showNotification('Store URL copied to clipboard!', 'success');
    } catch (error) {
      showNotification('Failed to copy URL', 'error');
    }
    setOpenDropdownId(null);
  };

  const handleViewLiveStore = (slug) => {
    window.open(`https://fitness1stsports.com/?team_store=${slug}`, '_blank');
    setOpenDropdownId(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'tsm-status-active';
      case 'draft': return 'tsm-status-draft';
      case 'closed': return 'tsm-status-closed';
      case 'archived': return 'tsm-status-archived';
      default: return 'tsm-status-draft';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'draft': return 'Draft';
      case 'closed': return 'Closed';
      case 'archived': return 'Archived';
      default: return status;
    }
  };

  // Filter, sort, and paginate stores
  const filteredStores = teamStores.filter((store) => {
    const matchesStatus = filterStatus === 'all' || store.status === filterStatus;
    if (!searchTerm.trim()) return matchesStatus;

    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      store.name?.toLowerCase().includes(searchLower) ||
      store.slug?.toLowerCase().includes(searchLower);

    return matchesStatus && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest': return new Date(a.created_at) - new Date(b.created_at);
      case 'name-asc': return a.name.localeCompare(b.name);
      case 'name-desc': return b.name.localeCompare(a.name);
      case 'revenue-desc': return (parseFloat(b.revenue) || 0) - (parseFloat(a.revenue) || 0);
      case 'orders-desc': return (parseInt(b.order_count) || 0) - (parseInt(a.order_count) || 0);
      default: return 0;
    }
  });

  const totalPages = Math.ceil(filteredStores.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStores = filteredStores.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="tsm-container">
        <div className="tsm-loading">
          <div className="tsm-spinner"></div>
          <p>Loading team stores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tsm-container">
      {/* Notification */}
      {notification && (
        <div className={`tsm-notification tsm-notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Create Store Modal */}
      <CreateStoreModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {deleteModalStore && (
        <DeleteStoreModal
          store={deleteModalStore}
          onConfirm={confirmDeleteStore}
          onCancel={() => setDeleteModalStore(null)}
        />
      )}

      {/* Header */}
      <div className="tsm-header">
        <div className="tsm-header-left">
          {selectedStore ? (
            <>
              <button className="tsm-back-btn" onClick={handleBackToStores}>
                <ChevronLeftIcon /> Back to Stores
              </button>
              <h1>{selectedStore.name}</h1>
            </>
          ) : (
            <>
              <span className="tsm-icon"><StoreIcon /></span>
              <h1>Team Store Management</h1>
            </>
          )}
        </div>
        <div className="tsm-header-right">
          {!selectedStore && (
            <>
              <button
                className="tsm-btn tsm-btn-secondary"
                onClick={() => navigate('/team-store-management/reports')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 20V10M12 20V4M6 20v-6"/>
                </svg>
                Reports
              </button>
              <button
                className="tsm-btn tsm-btn-create"
                onClick={() => setIsCreateModalOpen(true)}
              >
                + Create New Store
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      {!selectedStore ? (
        <div className="tsm-stores-section">
          {/* Controls Section */}
          <div className="tsm-controls-section">
            <div className="tsm-controls-top-row">
              <div className="tsm-search-filter">
                <input
                  type="text"
                  placeholder="Search stores..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="tsm-search-input"
                />

                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="tsm-filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="closed">Closed</option>
                </select>

                {creators.length > 0 && (
                  <select
                    value={filterCreator}
                    onChange={(e) => {
                      setFilterCreator(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="tsm-filter-select"
                  >
                    <option value="all">All Creators</option>
                    {creators.map((creator) => (
                      <option key={creator.id} value={creator.id}>
                        {creator.name}
                      </option>
                    ))}
                  </select>
                )}

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="tsm-filter-select"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="revenue-desc">Revenue (High-Low)</option>
                  <option value="orders-desc">Orders (High-Low)</option>
                </select>
              </div>

              <div className="tsm-controls-right">
                <div className="tsm-view-toggle">
                  <button
                    className={`tsm-view-btn ${viewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setViewMode('table')}
                    title="Table View"
                  >
                    <TableIcon />
                  </button>
                  <button
                    className={`tsm-view-btn ${viewMode === 'card' ? 'active' : ''}`}
                    onClick={() => setViewMode('card')}
                    title="Card View"
                  >
                    <GridIcon />
                  </button>
                </div>

                <button
                  className={`tsm-btn tsm-btn-ghost ${showArchived ? 'active' : ''}`}
                  onClick={handleToggleArchived}
                >
                  <ArchiveIcon /> {showArchived ? 'Hide Archived' : 'Archived'}
                </button>
              </div>
            </div>

            {/* Mobile Status Pills */}
            <div className="tsm-mobile-pills">
              {(() => {
                const counts = getStatusCounts();
                return (
                  <>
                    <button
                      className={`tsm-mobile-pill ${filterStatus === 'all' ? 'active' : ''}`}
                      onClick={() => { setFilterStatus('all'); setCurrentPage(1); }}
                    >
                      All<span className="tsm-mobile-pill-count">{counts.all}</span>
                    </button>
                    <button
                      className={`tsm-mobile-pill ${filterStatus === 'active' ? 'active' : ''}`}
                      onClick={() => { setFilterStatus('active'); setCurrentPage(1); }}
                    >
                      Active<span className="tsm-mobile-pill-count">{counts.active}</span>
                    </button>
                    <button
                      className={`tsm-mobile-pill ${filterStatus === 'draft' ? 'active' : ''}`}
                      onClick={() => { setFilterStatus('draft'); setCurrentPage(1); }}
                    >
                      Draft<span className="tsm-mobile-pill-count">{counts.draft}</span>
                    </button>
                    <button
                      className={`tsm-mobile-pill ${filterStatus === 'closed' ? 'active' : ''}`}
                      onClick={() => { setFilterStatus('closed'); setCurrentPage(1); }}
                    >
                      Closed<span className="tsm-mobile-pill-count">{counts.closed}</span>
                    </button>
                    <button className="tsm-mobile-filter-btn" onClick={() => setSortBy(sortBy === 'newest' ? 'revenue-desc' : 'newest')}>
                      <FilterIcon />
                    </button>
                  </>
                );
              })()}
            </div>

            <div className="tsm-controls-bottom-row">
              <div className="tsm-results-info">
                Showing {paginatedStores.length} of {filteredStores.length} stores
                {filterStatus !== 'all' && ` (${filterStatus})`}
              </div>
              <div className="tsm-pagination-controls">
                <span>Show:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="tsm-items-per-page"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Empty State */}
          {teamStores.length === 0 ? (
            <div className="tsm-empty-state">
              <span className="tsm-empty-icon"><StoreIcon /></span>
              <h3>No Team Stores Yet</h3>
              <p>Create your first team store to get started.</p>
              <button
                className="tsm-btn tsm-btn-primary"
                onClick={() => setIsCreateModalOpen(true)}
              >
                + Create New Store
              </button>
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="tsm-empty-state">
              <h3>No stores found</h3>
              <p>Try adjusting your search or filters.</p>
            </div>
          ) : (
            <>
              {/* Table View */}
              {viewMode === 'table' && (
                <div className="tsm-table-container">
                  <table className="tsm-stores-table">
                    <thead>
                      <tr>
                        <th>Store</th>
                        <th>Status</th>
                        <th>Created By</th>
                        <th>Products</th>
                        <th>Orders</th>
                        <th>Revenue</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStores.map((store) => (
                        <tr key={store.id}>
                          <td className="tsm-store-name-cell">
                            <div className="tsm-store-info">
                              {store.logo_url ? (
                                <img src={store.logo_url} alt="" className="tsm-table-logo" />
                              ) : (
                                <div className="tsm-table-initials">{store.logo_initials || 'TS'}</div>
                              )}
                              <div className="tsm-store-name-wrapper">
                                <span className="tsm-store-name">{store.name}</span>
                                {store.deadline && (
                                  <span className="tsm-store-deadline-hint">
                                    Deadline: {new Date(store.deadline).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`tsm-status ${getStatusClass(store.status)}`}>
                              {getStatusLabel(store.status)}
                            </span>
                          </td>
                          <td className="tsm-cell-creator">{store.creator_name || 'Unknown'}</td>
                          <td className="tsm-cell-number">{store.product_count || 0}</td>
                          <td className="tsm-cell-number">{store.order_count || 0}</td>
                          <td className="tsm-cell-number">{formatCurrency(store.revenue)}</td>
                          <td>
                            <button
                              className="tsm-actions-trigger"
                              onClick={(e) => handleDropdownToggle(store.id, e)}
                            >
                              <MoreIcon />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Card View */}
              {viewMode === 'card' && (
                <div className="tsm-compact-cards-grid">
                  {paginatedStores.map((store) => (
                    <div key={store.id} className={`tsm-compact-card tsm-card-${store.status}`}>
                      <div className="tsm-compact-header">
                        {store.logo_url ? (
                          <img src={store.logo_url} alt="" className="tsm-compact-logo" />
                        ) : (
                          <div className="tsm-compact-initials">{store.logo_initials || 'TS'}</div>
                        )}
                        <span className={`tsm-status-dot tsm-dot-${store.status}`} title={getStatusLabel(store.status)} />
                      </div>
                      <h4 className="tsm-compact-name">{store.name}</h4>
                      <div className="tsm-compact-stats">
                        <span>{store.product_count || 0} products</span>
                        <span>{store.order_count || 0} orders</span>
                      </div>
                      <div className="tsm-compact-revenue">
                        {formatCurrency(store.revenue)}
                      </div>
                      <div className="tsm-compact-actions">
                        <button
                          className="tsm-compact-action"
                          onClick={() => handleEditStore(store.id)}
                        >
                          Manage
                        </button>
                        <button
                          className="tsm-actions-trigger tsm-actions-trigger-card"
                          onClick={(e) => handleDropdownToggle(store.id, e)}
                        >
                          <MoreIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Mobile Cards */}
              <div className="tsm-mobile-cards">
                {paginatedStores.map((store) => (
                  <div
                    key={store.id}
                    className="tsm-mobile-card"
                    onClick={() => handleMobileCardTap(store)}
                  >
                    <div className="tsm-mobile-card-header">
                      {store.logo_url ? (
                        <img src={store.logo_url} alt="" className="tsm-mobile-card-logo" />
                      ) : (
                        <div className="tsm-mobile-card-initials">{store.logo_initials || 'TS'}</div>
                      )}
                      <div className="tsm-mobile-card-info">
                        <h4 className="tsm-mobile-card-name">{store.name}</h4>
                        <div className="tsm-mobile-card-subtitle">
                          <span className={`tsm-mobile-card-dot ${store.status}`} />
                          <span>{getStatusLabel(store.status)}</span>
                          <span>·</span>
                          <span>{store.order_count || 0} orders</span>
                        </div>
                      </div>
                    </div>
                    <div className="tsm-mobile-card-footer">
                      <div className="tsm-mobile-card-metrics">
                        <div className="tsm-mobile-card-metric">
                          <span className="tsm-mobile-card-metric-value">{store.order_count || 0}</span>
                          <span className="tsm-mobile-card-metric-label">orders</span>
                        </div>
                        <div className="tsm-mobile-card-metric">
                          <span className="tsm-mobile-card-metric-value">{formatCurrency(store.revenue)}</span>
                          <span className="tsm-mobile-card-metric-label">revenue</span>
                        </div>
                      </div>
                      {store.deadline && (
                        <div className="tsm-mobile-card-deadline">
                          Due {new Date(store.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="tsm-pagination">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="tsm-btn-pagination"
                  >
                    <ChevronLeftIcon /> Previous
                  </button>

                  <span className="tsm-page-info">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="tsm-btn-pagination"
                  >
                    Next <ChevronRightIcon />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Archived Stores Section */}
          {showArchived && (
            <div className="tsm-archived-section">
              <h2>Archived Stores</h2>
              {archivedLoading ? (
                <div className="tsm-loading">
                  <div className="tsm-spinner"></div>
                  <p>Loading archived stores...</p>
                </div>
              ) : archivedStores.length === 0 ? (
                <div className="tsm-empty-state tsm-empty-archived">
                  <span className="tsm-empty-icon"><ArchiveIcon /></span>
                  <h3>No Archived Stores</h3>
                  <p>Archived stores will appear here.</p>
                </div>
              ) : (
                <div className="tsm-archived-table-container">
                  <table className="tsm-stores-table">
                    <thead>
                      <tr>
                        <th>Store</th>
                        <th>Products</th>
                        <th>Archived</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedStores.map((store) => (
                        <tr key={store.id}>
                          <td className="tsm-store-name-cell">
                            <div className="tsm-store-info">
                              {store.logo_url ? (
                                <img src={store.logo_url} alt="" className="tsm-table-logo" />
                              ) : (
                                <div className="tsm-table-initials">{store.logo_initials || 'TS'}</div>
                              )}
                              <span>{store.name}</span>
                            </div>
                          </td>
                          <td className="tsm-cell-number">{store.product_count || 0}</td>
                          <td>{formatDate(store.updated_at)}</td>
                          <td>
                            <div className="tsm-archived-actions">
                              <button
                                className="tsm-btn tsm-btn-small"
                                onClick={() => handleUnarchiveStore(store.id)}
                              >
                                Restore
                              </button>
                              <button
                                className="tsm-btn tsm-btn-small tsm-btn-danger"
                                onClick={() => setDeleteModalStore(store)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        // Orders View for Selected Store
        <div className="tsm-orders-section">
          <div className="tsm-orders-header">
            <div className="tsm-orders-title-row">
              <h2>Orders for {selectedStore.name}</h2>
              {runs.length > 1 && (
                <select
                  className="tsm-run-select"
                  value={selectedRunId || ''}
                  onChange={(e) => handleRunChange(e.target.value === 'all' ? 'all' : (e.target.value ? parseInt(e.target.value) : null))}
                >
                  {runs.map((run) => (
                    <option key={run.id} value={run.id}>
                      {formatRunLabel(run)}
                    </option>
                  ))}
                  <option value="all">All Orders</option>
                </select>
              )}
              {runs.length === 1 && runs[0] && (
                <span className="tsm-run-label">
                  {new Date(runs[0].openedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' – '}
                  {runs[0].closedAt
                    ? new Date(runs[0].closedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Present'}
                </span>
              )}
            </div>
            <div className="tsm-orders-actions">
              <button
                className="tsm-btn tsm-btn-primary"
                onClick={() => generateReport(selectedStore.slug, selectedRunId)}
                disabled={reportGenerating}
              >
                {reportGenerating ? 'Generating...' : 'Generate Fulfillment Report'}
              </button>
              <button
                className="tsm-btn tsm-btn-secondary"
                onClick={() => printInvoices(selectedStore.slug, selectedRunId)}
                disabled={invoicesGenerating}
              >
                {invoicesGenerating ? 'Generating...' : 'Print Invoices'}
              </button>
              <button
                className="tsm-btn tsm-btn-success"
                onClick={handleMarkAllComplete}
                disabled={markingComplete || orders.length === 0}
              >
                Mark All Completed
              </button>
              <button
                className="tsm-btn tsm-btn-secondary"
                onClick={() => fetchOrders(selectedStore.slug, selectedRunId)}
              >
                Refresh
              </button>
            </div>
            {selectedOrders.length > 0 && (
              <div className="tsm-orders-selection-actions">
                <span className="tsm-selection-count">{selectedOrders.length} order{selectedOrders.length !== 1 ? 's' : ''} selected</span>
                <button
                  className="tsm-btn tsm-btn-success"
                  onClick={handleMarkSelectedComplete}
                  disabled={markingComplete}
                >
                  {markingComplete ? 'Marking...' : 'Mark Selected as Completed'}
                </button>
                <button
                  className="tsm-btn tsm-btn-ghost"
                  onClick={() => setSelectedOrders([])}
                >
                  Clear Selection
                </button>
              </div>
            )}
          </div>

          {ordersLoading ? (
            <div className="tsm-loading">
              <div className="tsm-spinner"></div>
              <p>Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="tsm-empty-state">
              <span className="tsm-empty-icon"><OrdersIcon /></span>
              <h3>No Orders Yet</h3>
              <p>Orders will appear here once customers place them.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="tsm-orders-table-container">
                <table className="tsm-orders-table">
                  <thead>
                    <tr>
                      <th className="tsm-checkbox-cell">
                        <input
                          type="checkbox"
                          checked={selectedOrders.length === orders.length && orders.length > 0}
                          onChange={handleSelectAllOrders}
                          title="Select all orders"
                        />
                      </th>
                      <th>Order #</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Shipping Address</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} className={selectedOrders.includes(order.id) ? 'tsm-row-selected' : ''}>
                        <td className="tsm-checkbox-cell">
                          <input
                            type="checkbox"
                            checked={selectedOrders.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                          />
                        </td>
                        <td className="tsm-order-number">#{order.number}</td>
                        <td>{formatDate(order.date)}</td>
                        <td>
                          <div className="tsm-customer">
                            <span className="tsm-customer-name">{order.customerName}</span>
                            <span className="tsm-customer-email">{order.customerEmail}</span>
                            {order.customerPhone && (
                              <span className="tsm-customer-phone">{order.customerPhone}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="tsm-address">
                            {order.shipping ? (
                              <>
                                <span className="tsm-address-line">{order.shipping.address_1}</span>
                                {order.shipping.address_2 && (
                                  <span className="tsm-address-line">{order.shipping.address_2}</span>
                                )}
                                <span className="tsm-address-line">
                                  {order.shipping.city}, {order.shipping.state} {order.shipping.postcode}
                                </span>
                              </>
                            ) : (
                              <span className="tsm-address-none">No address</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="tsm-items">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="tsm-item">
                                {item.name}{item.design && <span className="tsm-item-design"> ({item.design})</span>} <span className="tsm-item-size">({item.size})</span>
                                {item.jerseyNumber && <span className="tsm-jersey-number">#{item.jerseyNumber}</span>}
                                {item.lastName && <span className="tsm-last-name">{item.lastName}</span>}
                                {' '}x{item.qty}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="tsm-total">{formatCurrency(order.total)}</td>
                        <td>
                          <span className={`tsm-order-status tsm-status-${order.status}`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="tsm-orders-mobile-list">
                {orders.map((order) => (
                  <div key={order.id} className={`tsm-order-card ${selectedOrders.includes(order.id) ? 'tsm-card-selected' : ''}`}>
                    <div className="tsm-order-card-header">
                      <div className="tsm-order-card-left">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => handleSelectOrder(order.id)}
                        />
                        <span className="tsm-order-card-number">#{order.number}</span>
                      </div>
                      <span className={`tsm-order-status tsm-status-${order.status}`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="tsm-order-card-body">
                      <div className="tsm-order-card-row">
                        <span className="tsm-order-card-label">Date</span>
                        <span className="tsm-order-card-value">{formatDate(order.date)}</span>
                      </div>
                      <div className="tsm-order-card-row">
                        <span className="tsm-order-card-label">Customer</span>
                        <div className="tsm-order-card-customer">
                          <span>{order.customerName}</span>
                          <span className="tsm-order-card-email">{order.customerEmail}</span>
                        </div>
                      </div>
                      <div className="tsm-order-card-row">
                        <span className="tsm-order-card-label">Items</span>
                        <div className="tsm-order-card-items">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="tsm-order-card-item">
                              {item.name}{item.design ? ` (${item.design})` : ''} ({item.size})
                              {item.jerseyNumber && <span className="tsm-jersey-number">#{item.jerseyNumber}</span>}
                              {item.lastName && <span className="tsm-last-name">{item.lastName}</span>}
                              {' '}x{item.qty}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="tsm-order-card-row">
                        <span className="tsm-order-card-label">Total</span>
                        <span className="tsm-order-card-total">{formatCurrency(order.total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Mark Complete Confirmation Modal */}
      {completeModal.show && (
        <div className="tsm-modal-overlay">
          <div className="tsm-modal tsm-modal-confirm">
            <h3>Confirm Mark as Completed</h3>
            <p>
              {completeModal.type === 'all'
                ? `Are you sure you want to mark ALL orders for "${selectedStore?.name}" as completed? This will update the order status on WooCommerce.`
                : `Are you sure you want to mark ${selectedOrders.length} selected order${selectedOrders.length !== 1 ? 's' : ''} as completed? This will update the order status on WooCommerce.`
              }
            </p>
            <div className="tsm-modal-actions">
              <button
                className="tsm-btn tsm-btn-secondary"
                onClick={() => setCompleteModal({ show: false, type: null })}
                disabled={markingComplete}
              >
                Cancel
              </button>
              <button
                className="tsm-btn tsm-btn-success"
                onClick={confirmMarkComplete}
                disabled={markingComplete}
              >
                {markingComplete ? 'Processing...' : 'Yes, Mark as Completed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portal-based dropdown menu - renders outside table container */}
      {openDropdownId && createPortal(
        <div
          ref={dropdownRef}
          className="tsm-dropdown-portal"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 9999,
          }}
        >
          <div className="tsm-dropdown-menu tsm-dropdown-menu-portal">
            {(() => {
              const store = [...teamStores, ...archivedStores].find(s => s.id === openDropdownId);
              if (!store) return null;
              return (
                <>
                  <button onClick={() => handleEditStore(store.id)}>
                    <EditIcon /> Edit Store
                  </button>
                  <button onClick={() => handleStoreSelect(store)}>
                    <OrdersIcon /> View Orders
                  </button>
                  {store.status === 'active' && (
                    <>
                      <button onClick={() => handleCopyStoreUrl(store.slug)}>
                        <CopyIcon /> Copy Store URL
                      </button>
                      <button onClick={() => handleViewLiveStore(store.slug)}>
                        <LinkIcon /> View Live Store
                      </button>
                    </>
                  )}
                  <div className="tsm-dropdown-divider" />
                  {store.status === 'draft' && (
                    <button onClick={() => handlePublishStore(store.id)} className="tsm-action-publish">
                      <PublishIcon /> Publish Store
                    </button>
                  )}
                  {store.status === 'active' && (
                    <button onClick={() => handleCloseStore(store.id)} className="tsm-action-close">
                      <CloseIcon /> Close Store
                    </button>
                  )}
                  {(store.status === 'closed' || store.status === 'draft') && (
                    <button onClick={() => handleArchiveStore(store.id)} className="tsm-action-archive">
                      <ArchiveIcon /> Archive Store
                    </button>
                  )}
                  <div className="tsm-dropdown-divider" />
                  <button onClick={() => { setOpenDropdownId(null); setDeleteModalStore(store); }} className="tsm-action-delete">
                    <DeleteIcon /> Delete Store
                  </button>
                </>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {/* Mobile FAB - Create New Store */}
      {!selectedStore && (
        <button
          className="tsm-mobile-fab"
          onClick={() => setIsCreateModalOpen(true)}
          aria-label="Create new store"
        >
          <PlusIcon />
        </button>
      )}

      {/* Mobile Bottom Sheet */}
      <div
        className={`tsm-bottom-sheet-overlay ${bottomSheetStore ? 'open' : ''}`}
        onClick={closeBottomSheet}
      />
      <div className={`tsm-bottom-sheet ${bottomSheetStore ? 'open' : ''}`}>
        {bottomSheetStore && (
          <>
            <div className="tsm-bottom-sheet-handle" />
            <div className="tsm-bottom-sheet-header">
              {bottomSheetStore.logo_url ? (
                <img src={bottomSheetStore.logo_url} alt="" className="tsm-bottom-sheet-logo" />
              ) : (
                <div className="tsm-bottom-sheet-initials">{bottomSheetStore.logo_initials || 'TS'}</div>
              )}
              <div className="tsm-bottom-sheet-info">
                <h3 className="tsm-bottom-sheet-name">{bottomSheetStore.name}</h3>
                <div className="tsm-bottom-sheet-meta">
                  <span className={`tsm-bottom-sheet-status ${bottomSheetStore.status}`}>
                    {getStatusLabel(bottomSheetStore.status)}
                  </span>
                  {bottomSheetStore.deadline && (
                    <span>Due {new Date(bottomSheetStore.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="tsm-bottom-sheet-stats">
              <div className="tsm-bottom-sheet-stat">
                <div className="tsm-bottom-sheet-stat-value">{bottomSheetStore.product_count || 0}</div>
                <div className="tsm-bottom-sheet-stat-label">Products</div>
              </div>
              <div className="tsm-bottom-sheet-stat">
                <div className="tsm-bottom-sheet-stat-value">{bottomSheetStore.order_count || 0}</div>
                <div className="tsm-bottom-sheet-stat-label">Orders</div>
              </div>
              <div className="tsm-bottom-sheet-stat">
                <div className="tsm-bottom-sheet-stat-value">{formatCurrency(bottomSheetStore.revenue)}</div>
                <div className="tsm-bottom-sheet-stat-label">Revenue</div>
              </div>
            </div>

            <div className="tsm-bottom-sheet-actions">
              <button
                className="tsm-bottom-sheet-action primary"
                onClick={() => { closeBottomSheet(); handleEditStore(bottomSheetStore.id); }}
              >
                <EditIcon /> Manage Store
              </button>

              <button
                className="tsm-bottom-sheet-action"
                onClick={() => { closeBottomSheet(); handleStoreSelect(bottomSheetStore); }}
              >
                <OrdersIcon /> View Orders
              </button>

              {bottomSheetStore.status === 'active' && (
                <>
                  <button
                    className="tsm-bottom-sheet-action"
                    onClick={() => { handleCopyStoreUrl(bottomSheetStore.slug); closeBottomSheet(); }}
                  >
                    <CopyIcon /> Copy Store URL
                  </button>
                  <button
                    className="tsm-bottom-sheet-action"
                    onClick={() => { handleViewLiveStore(bottomSheetStore.slug); closeBottomSheet(); }}
                  >
                    <LinkIcon /> View Live Store
                  </button>
                </>
              )}

              <div className="tsm-bottom-sheet-divider" />

              {bottomSheetStore.status === 'draft' && (
                <button
                  className="tsm-bottom-sheet-action success"
                  onClick={() => { handlePublishStore(bottomSheetStore.id); closeBottomSheet(); }}
                >
                  <PublishIcon /> Publish Store
                </button>
              )}

              {bottomSheetStore.status === 'active' && (
                <button
                  className="tsm-bottom-sheet-action danger"
                  onClick={() => { handleCloseStore(bottomSheetStore.id); closeBottomSheet(); }}
                >
                  <CloseIcon /> Close Store
                </button>
              )}

              {(bottomSheetStore.status === 'closed' || bottomSheetStore.status === 'draft') && (
                <button
                  className="tsm-bottom-sheet-action"
                  onClick={() => { handleArchiveStore(bottomSheetStore.id); closeBottomSheet(); }}
                >
                  <ArchiveIcon /> Archive Store
                </button>
              )}

              <div className="tsm-bottom-sheet-divider" />

              <button
                className="tsm-bottom-sheet-action danger"
                onClick={() => { closeBottomSheet(); setDeleteModalStore(bottomSheetStore); }}
              >
                <DeleteIcon /> Delete Store
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
